import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import authApi from "../../../service/IdApi";
import { useAppContext } from "../../../context/appContext";

const FIELD_LABEL_WIDTH = "150px";

const AdminProviderDetailsDialog = ({ visible, onHide, providerId }) => {
    const { displayAlert } = useAppContext();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!visible || !providerId) return;
        let cancelled = false;
        setIsLoading(true);
        setData(null);
        authApi
            .post("admin/provider/full-details?providerId=" + encodeURIComponent(providerId), {})
            .then((res) => {
                if (!cancelled) setData(res.data);
            })
            .catch((err) => {
                if (!cancelled) {
                    displayAlert("error", "Error", "Failed to load provider details");
                    console.error(err);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => { cancelled = true; };
    }, [visible, providerId]);

    const Field = ({ label, value }) => (
        <div className="field grid mb-0">
            <label className="col-fixed" style={{ width: FIELD_LABEL_WIDTH }}>{label}</label>
            <div className="col">{value ?? "—"}</div>
        </div>
    );

    if (!visible) return null;

    return (
        <Dialog
            visible={visible}
            style={{ width: "50vw" }}
            breakpoints={{ "1280px": "75vw", "641px": "95vw" }}
            header="Provider Details"
            modal
            onHide={onHide}
            className="p-fluid"
        >
            {isLoading ? (
                <div className="flex justify-content-center py-4">
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }} />
                </div>
            ) : data ? (
                <div>
                    {data.provider && (
                        <>
                            <div className="text-xl font-semibold mb-2">Provider</div>
                            <Field label="Name" value={data.provider.name} />
                            <Field label="Provider ID" value={providerId} />
                            <Field label="Provider Number" value={data.provider.providerNumber} />
                            <Field label="Phone Number" value={data.provider.phoneNumber} />
                            <Field label="Address" value={data.provider.address} />
                            <Field label="City" value={data.provider.city} />
                            <Field label="State Code" value={data.provider.stateCode} />
                            <Field label="Zip Code" value={data.provider.zipCode} />
                            <Field label="Timezone" value={data.provider.timezone} />
                            <Field label="Website" value={data.provider.website} />
                            <Field label="Unclaimed" value={data.provider.unclaimed ? "Yes" : "No"} />
                            <Field label="Created On" value={data.provider.createdAt} />
                        </>
                    )}
                    {data.subscription != null && (
                        <>
                            <div className="text-xl font-semibold mb-2 mt-3">Subscription</div>
                            <Field label="Status" value={data.subscription.status} />
                            <Field label="Credit Card on File" value={data.subscription.cc ? "Yes" : "No"} />
                            <Field label="Trial Start" value={data.subscription.trialStartAt} />
                            <Field label="Trial End" value={data.subscription.trialEndAt} />
                            <Field label="Subscription Start" value={data.subscription.subscriptionStartAt} />
                            <Field label="Subscription End" value={data.subscription.subscriptionEndAt} />
                            <Field label="Cycle" value={data.subscription.cycle} />
                            <Field label="Next Bill At" value={data.subscription.nextBillAt} />
                            <Field label="Credits" value={data.subscription.credits} />
                            <Field label="Send Receipts" value={data.subscription.sendReceipts ? "Yes" : "No"} />
                            {data.subscription.entitlements && data.subscription.entitlements.length > 0 && (
                                <>
                                    <div className="field grid mb-1">
                                        <label className="col-fixed" style={{ width: FIELD_LABEL_WIDTH }}>Entitlements</label>
                                        <div className="col" />
                                    </div>
                                    <div className="ml-4 mb-2">
                                        <DataTable
                                            value={data.subscription.entitlements}
                                            size="small"
                                            stripedRows
                                            className="p-datatable-sm"
                                        >
                                            <Column field="name" header="Name" />
                                            <Column field="type" header="Type" />
                                            <Column field="amount" header="Amount" body={(row) => row.amount ? `$${row.amount}` : "—"} />
                                            <Column field="cycle" header="Cycle" body={(row) => row.cycle === "m" ? "Monthly" : row.cycle === "y" ? "Yearly" : row.cycle ?? "—"} />
                                            <Column field="userCount" header="Users" />
                                            <Column field="startAt" header="Start" />
                                        </DataTable>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                    {data.provider == null && data.subscription == null && (
                        <div className="text-color-secondary">No provider or subscription data.</div>
                    )}
                </div>
            ) : null}
        </Dialog>
    );
};

export default AdminProviderDetailsDialog;
